/** @file FacetEntrySection: one facet entry of a drawable at a time (table, bands, frames, variants, depth). */
import type { PuppetInputs } from '@zilo/pose3d-commands/types/puppetInputs';
import { PITCH_RANGES } from '@zilo/pose3d-format/tables/pitchRanges';
import { SECTOR_TABLES } from '@zilo/pose3d-format/tables/sectorTables';
import type { FacetEntry, PitchRangeName } from '@zilo/pose3d-format/types/facet';
import type { ReactElement } from 'react';
import { useEditor } from '../../state/useEditor';
import { useEditorStore } from '../../state/useEditorStore';
import { NumericField } from '../../ui-kit/numericInput/NumericField';
import { NumericRow } from '../../ui-kit/numericInput/NumericRow';
import { InspectorSection } from './InspectorSection';
import { ListInput } from './ListInput';

/** The fields `facet.setEntry` merges. */
type EntryFields = PuppetInputs['facet.setEntry']['fields'];

const row = { display: 'flex', alignItems: 'center', gap: 6 };
const label = { width: 92, color: 'var(--muted, #8b919e)' };
const BANDS = Object.keys(PITCH_RANGES) as PitchRangeName[];
const parseFrame = (t: string): number | null => (/^\d+$/.test(t) ? Number(t) : null);
const parseVariant = (t: string): string | null => (/^[\w.-]+$/.test(t) ? t : null);

/** Lookup fields: sector table, pitch bands, frame keys, variant flags. */
function LookFields({ e, tables, set, setTable }: { e: FacetEntry; tables: string[]; set: (f: EntryFields) => void; setTable: (id: string) => void }): ReactElement {
  return (
    <>
      <label style={row}><span style={label}>yaw table</span>
        <select aria-label="yaw table" value={e.yaw} onChange={(ev) => { setTable(ev.currentTarget.value); }}>
          {tables.map((t) => <option key={t} value={t}>{t}</option>)}
        </select></label>
      <label style={row}><span style={label}>bands</span>
        <select aria-label="bands" multiple size={4} value={e.bands}
          onChange={(ev) => { const b = [...ev.currentTarget.selectedOptions].map((o) => o.value as PitchRangeName); if (b.length > 0) set({ bands: b }); }}>
          {BANDS.map((b) => <option key={b} value={b}>{b}</option>)}
        </select></label>
      <div style={row}><span style={label}>frames</span><ListInput label="frames" value={e.frames} parse={parseFrame} onCommit={(frames) => { set({ frames }); }} /></div>
      <div style={row}><span style={label}>variants</span><ListInput label="variants" value={e.variants} parse={parseVariant} onCommit={(variants) => { set({ variants }); }} /></div>
    </>
  );
}

/** The depth extras (side and back px); the anchor, size, rotate rule and cut live in the Rig block. */
function DepthFields({ e, set }: { e: FacetEntry; set: (f: EntryFields) => void }): ReactElement {
  return (
    <>
      <NumericRow label="depth" unit="px">
        <NumericField label="depth side" handle="side" value={e.depth?.side ?? 0} step={1} onCommit={(side) => { set({ depth: { ...e.depth, side } }); }} />
        <NumericField label="depth back" handle="back" value={e.depth?.back ?? 0} step={1} onCommit={(back) => { set({ depth: { ...e.depth, back } }); }} />
      </NumericRow>
    </>
  );
}

/**
 * Entries of drawable `id` (impostor or effect); `entry` is the picked index (the inspector holds it, the Rig block
 * edits the same entry) and `onEntry` picks another; edits are `facet.setEntry` / `facet.setTable` on it.
 */
export function FacetEntrySection({ id, entry, onEntry }: { id: string; entry: number; onEntry: (entry: number) => void }): ReactElement | null {
  const store = useEditorStore();
  const d = useEditor((s) => s.puppet.doc?.puppet.drawables.find((x) => x.id === id));
  const own = useEditor((s) => s.puppet.doc?.puppet.sectorTables);
  const facets = d?.kind === 'impostor' || d?.kind === 'effect' ? (d.facets ?? []) : [];
  const index = Math.max(0, Math.min(entry, facets.length - 1));
  const e = facets[index];
  if (e === undefined) return null;
  const s = store.getState();
  const set = (fields: EntryFields): void => { void s.dispatch([{ name: 'facet.setEntry', input: { drawable: id, entry: index, fields } }], `set ${id}[${index}]`); };
  const setTable = (table: string): void => { void s.dispatch([{ name: 'facet.setTable', input: { drawable: id, entry: index, table } }], `table ${id}[${index}]`); };
  const openArt = (): void => {
    const puppetId = s.puppet.id;
    if (puppetId !== null) s.navigate({ screen: 'art', puppetId });
    s.select({ drawable: id, slot: `${id}/${index}/0` });
  };
  return (
    <InspectorSection title="Facet entry" aside={`${e.sheet} ${e.size[0]}x${e.size[1]} @${e.origin.join(',')}, ${Object.keys(e.slots).length} filled`}>
      <label style={row}><span style={label}>entry</span>
        <select aria-label="entry" value={index} onChange={(ev) => { onEntry(Number(ev.currentTarget.value)); }}>
          {facets.map((f, i) => <option key={i} value={i}>{i}: {f.yaw} {f.bands.join('/')}</option>)}
        </select>
        <button type="button" onClick={openArt} style={{ font: 'inherit', fontSize: 11 }}>Open in Art</button></label>
      <LookFields e={e} tables={[...Object.keys(SECTOR_TABLES), ...Object.keys(own ?? {})]} set={set} setTable={setTable} />
      <DepthFields e={e} set={set} />
    </InspectorSection>
  );
}
